import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import Markdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import { ApprovalCard, fetchApprovals, type Approval } from "./Approvals.tsx";
import { api, unwrap } from "../api.ts";
import { useMe } from "../hooks.ts";
import { cx, ErrorText, Spinner } from "../components/ui.tsx";
import { FilePreview } from "../components/FilePreview.tsx";
import { readSse } from "../lib/sse.ts";
import { fileUrl, isImage, uploadFile, workspaceRel, WORKSPACE } from "../lib/files.ts";
import { WorkspacePicker, type Picked } from "../components/WorkspacePicker.tsx";
import { TaskCheck, useTasks } from "../components/TimeSaved.tsx";
import type { ShellContext } from "../components/Shell.tsx";

import { toTurns, type Attachment, type Tool, type Turn } from "../lib/transcript.ts";
import { Icon } from "../components/icons.tsx";

/** Hermes streams images as inline data URLs; react-markdown drops data: URLs unless allowed. Raster only. */
const mediaUrlTransform = (url: string) => (/^data:image\/(png|jpe?g|gif|webp);base64,/.test(url) ? url : defaultUrlTransform(url));

/**
 * Saved replies keep the agent's `MEDIA:<path>` tags (only the live stream inlines them). Workspace
 * images render inline from the file API; other files become download links.
 */
function withMedia(text: string): string {
  return text.replace(/MEDIA:\s*(\S+)/g, (whole, p: string) => {
    const rel = workspaceRel(p.replace(/[.,;:!?)]+$/, ""));
    if (!rel) return whole;
    const name = rel.split("/").pop() ?? rel;
    return isImage(rel) ? `![${name}](${fileUrl(rel)})` : `[${name}](${fileUrl(rel, true)})`;
  });
}

const prettyTool = (n: string) =>
  n.replace(/^mcp__composio__COMPOSIO_/, "").replace(/^mcp__\w+__/, "").replace(/_/g, " ").toLowerCase();

const MESSAGE_PAGE = 150;

/* ── Page ──────────────────────────────────────────────────────────────── */

export function ChatPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const canUpload = me.data?.group?.permissions.fileUpload ?? false;

  // Messages load a page at a time; older pages are fetched on demand. The chat list is in the menu.
  // After an approval decision (or while one is pending) the assistant continues on its own: keep the chat fresh.
  const [followUntil, setFollowUntil] = useState(0);
  const sessionApprovals = useQuery({
    queryKey: ["approvals", "session", sessionId],
    queryFn: () => fetchApprovals({ sessionId: sessionId! }),
    enabled: !!sessionId,
    refetchInterval: 15_000,
  });
  const waiting = (sessionApprovals.data?.approvals ?? []).filter((a) => a.status === "pending");
  const following = followUntil > Date.now() || waiting.length > 0;
  const history = useInfiniteQuery({
    refetchInterval: following ? 5_000 : false,
    queryKey: ["chat-messages", sessionId],
    queryFn: ({ pageParam }) => unwrap(api.me.assistant.sessions[":id"].messages.$get({ param: { id: sessionId! }, query: { limit: String(MESSAGE_PAGE), offset: String(pageParam) } })),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (last.hasMore ? all.length * MESSAGE_PAGE : undefined),
    enabled: !!sessionId,
  });
  // Pages arrive newest-first; the conversation reads oldest-first.
  const historyMessages = useMemo(() => (history.data ? [...history.data.pages].reverse().flatMap((p) => p.messages) : null), [history.data]);

  const [turns, setTurns] = useState<Turn[]>([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const runId = useRef<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  // The session a stream is writing into; stops history reloads from clobbering live turns.
  const liveSession = useRef<string | null>(null);
  // History fetched before the last stream ended is stale (it predates the reply being saved).
  const streamEndedAt = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      if (!busy) setTurns([]);
      return;
    }
    if (historyMessages && liveSession.current !== sessionId && history.dataUpdatedAt >= streamEndedAt.current) setTurns(toTurns(historyMessages));
  }, [sessionId, historyMessages, history.dataUpdatedAt, busy]);

  const patchLast = (fn: (t: Turn) => Turn) => setTurns((ts) => (ts.length ? [...ts.slice(0, -1), fn(ts[ts.length - 1]!)] : ts));

  /** Draws a turn's events into the last (assistant) turn: text, tool steps, errors. */
  const readTurn = useCallback(async (body: ReadableStream<Uint8Array>) => {
    for await (const { event, data } of readSse(body)) {
      if (typeof data.run_id === "string") runId.current = data.run_id;
      if (event === "krut.turn") {
        // A replay opens with the question being answered: the reply goes under it (what had been saved of
        // this reply so far is redrawn from the replay), or the question is added if it isn't saved yet.
        const asked = String(data.text ?? "").trim();
        setTurns((ts) => {
          const i = ts.findLastIndex((t) => t.role === "user");
          const base = i >= 0 && ts[i]!.text.trim() === asked ? ts.slice(0, i + 1) : [...ts, { key: `u${Date.now()}`, role: "user" as const, text: asked }];
          return [...base, { key: `a${Date.now()}`, role: "assistant", text: "", tools: [], streaming: true }];
        });
      } else if (event === "assistant.delta") patchLast((t) => ({ ...t, text: t.text + String(data.delta ?? "") }));
      else if (event === "tool.started")
        patchLast((t) => ({ ...t, tools: [...(t.tools ?? []), { name: String(data.tool_name), preview: data.preview ? String(data.preview) : undefined, status: "running", startedAt: Date.now() }] }));
      else if (event === "tool.completed" || event === "tool.failed") {
        const status = event === "tool.completed" ? "done" : "failed";
        patchLast((t) => {
          const tools = [...(t.tools ?? [])];
          const i = tools.map((x) => x.name).lastIndexOf(String(data.tool_name));
          if (i >= 0) tools[i] = { ...tools[i]!, status };
          return { ...t, tools };
        });
      } else if (event === "assistant.completed") patchLast((t) => ({ ...t, text: String(data.content ?? t.text) || t.text }));
      else if (event === "run.failed") patchLast((t) => ({ ...t, error: String((data as { error?: unknown }).error ?? "The assistant hit an error.") }));
    }
  }, []);

  /** After a stream ends: settle the reply, then reload the saved transcript (titles, tool calls). */
  const settle = useCallback(
    (sid: string | undefined) => {
      if (sid) resumed.current = sid; // nothing left to pick up in this chat
      patchLast((t) => ({ ...t, streaming: false, tools: (t.tools ?? []).map((x) => (x.status === "running" ? { ...x, status: "done" } : x)) }));
      setBusy(false);
      runId.current = null;
      abort.current = null;
      liveSession.current = null;
      streamEndedAt.current = Date.now();
      if (sid) void qc.invalidateQueries({ queryKey: ["chat-messages", sid] });
      void qc.invalidateQueries({ queryKey: ["chat-sessions"] });
    },
    [qc],
  );

  // A reloaded page (or another tab) picks up a reply that is still being written: everything so far is
  // replayed, then it follows live — instead of showing only what had been saved.
  const resumed = useRef<string | null>(null);
  const resuming = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!sessionId || !historyMessages || busy || liveSession.current || resumed.current === sessionId) return;
    resumed.current = sessionId;
    const sid = sessionId;
    const ctrl = new AbortController();
    resuming.current = ctrl;
    void (async () => {
      const r = await fetch(`/api/me/assistant/sessions/${encodeURIComponent(sid)}/live`, { signal: ctrl.signal }).catch(() => null);
      if (!r || r.status !== 200 || !r.body || ctrl.signal.aborted) return;
      abort.current = ctrl;
      liveSession.current = sid;
      setBusy(true);
      try {
        await readTurn(r.body);
      } catch (e) {
        if ((e as Error).name !== "AbortError") patchLast((t) => ({ ...t, error: (e as Error).message }));
      } finally {
        settle(sid);
      }
    })();
  }, [sessionId, historyMessages, busy, readTurn, settle]);
  // Leaving the chat stops following it (the reply carries on and is saved).
  useEffect(
    () => () => {
      resuming.current?.abort();
      resuming.current = null;
    },
    [sessionId],
  );

  const send = useCallback(
    async (text: string, attachments: Attachment[], context: Picked[] = []) => {
      setBusy(true);
      setTurns((ts) => [...ts, { key: `u${Date.now()}`, role: "user", text, attachments, context }, { key: `a${Date.now()}`, role: "assistant", text: "", tools: [], streaming: true }]);
      const ctrl = new AbortController();
      abort.current = ctrl;
      liveSession.current = sessionId ?? "new";
      let activeSid = sessionId;
      try {
        const r = await fetch("/api/me/assistant/chat", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ sessionId, text, attachments, context }),
          signal: ctrl.signal,
        });
        if (!r.ok || !r.body) throw new Error(((await r.json().catch(() => ({}))) as { error?: string }).error ?? `Request failed (${r.status})`);
        const sid = r.headers.get("x-session-id");
        if (sid) activeSid = sid;
        if (sid && sid !== sessionId) {
          liveSession.current = sid;
          navigate(`/chat/${sid}`, { replace: true });
        }
        await readTurn(r.body);
      } catch (e) {
        if ((e as Error).name !== "AbortError") patchLast((t) => ({ ...t, error: (e as Error).message }));
      } finally {
        settle(activeSid);
      }
    },
    [sessionId, navigate, readTurn, settle],
  );

  const stop = async () => {
    const id = runId.current;
    if (id) await fetch(`/api/me/assistant/runs/${encodeURIComponent(id)}/stop`, { method: "POST" }).catch(() => {});
    abort.current?.abort();
  };

  // The menu's chat list and New chat wait while a reply streams in.
  const setChatBusy = useOutletContext<ShellContext | undefined>()?.setChatBusy;
  useEffect(() => {
    setChatBusy?.(busy);
    return () => setChatBusy?.(false);
  }, [setChatBusy, busy]);

  return (
    <div className="relative flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <Conversation
          waiting={waiting}
          onDecided={() => setFollowUntil(Date.now() + 5 * 60_000)}
          turns={turns}
          loading={!!sessionId && history.isPending && !busy}
          onOpenFile={setPreview}
          onPick={(t) => void send(t, [])}
          earlier={history.hasNextPage && !busy ? { loading: history.isFetchingNextPage, load: () => void history.fetchNextPage() } : null}
        />
        <Composer busy={busy} canUpload={canUpload} onSend={send} onStop={stop} onOpenFile={setPreview} />
      </div>
      <FilePreview path={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

/* ── Conversation ──────────────────────────────────────────────────────── */

const SUGGESTIONS = [
  "What can you help me with?",
  "Summarise the files in my workspace",
  "Draft a weekly status update for my team",
  "Set up a daily 9am briefing of my GitHub notifications",
];

function Conversation({
  turns,
  loading,
  onOpenFile,
  onPick,
  earlier,
  waiting = [],
  onDecided,
}: {
  turns: Turn[];
  loading: boolean;
  onOpenFile: (p: string) => void;
  onPick: (text: string) => void;
  earlier: { loading: boolean; load: () => void } | null;
  waiting?: Approval[];
  onDecided?: () => void;
}) {
  const end = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const prev = useRef<{ first?: string; last?: string; height: number }>({ height: 0 });
  const me = useMe();
  // Finished tasks the assistant recorded in these replies: each reply asks whether it saved time.
  const recorded = useTasks(useMemo(() => turns.flatMap((t) => (t.streaming ? [] : (t.tasks ?? []))), [turns]));
  // Block body on purpose: Chrome 152+ returns a Promise from scrollIntoView, and an arrow
  // that returns it would hand React a "cleanup" that isn't a function (crashes on update).
  useEffect(() => {
    const el = scroller.current;
    const first = turns[0]?.key;
    const last = turns[turns.length - 1]?.key;
    // Older messages were added on top: keep the reader where they were instead of jumping to the end.
    if (el && prev.current.last === last && prev.current.first !== first && prev.current.height) {
      el.scrollTop += el.scrollHeight - prev.current.height;
    } else {
      void end.current?.scrollIntoView({ block: "end" });
    }
    prev.current = { first, last, height: el?.scrollHeight ?? 0 };
  }, [turns]);

  if (loading) return <div className="grid flex-1 place-items-center"><Spinner /></div>;
  if (!turns.length) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
        <h1 className="text-[28px] leading-tight font-semibold">Hi {me.data?.user.name.split(" ")[0]}, what can I do for you?</h1>
        <p className="mt-2 text-[14.5px] text-[var(--muted)]">Your assistant can use your files, connected apps and the web.</p>
        <div className="mt-8 grid w-full max-w-2xl gap-2 sm:grid-cols-2">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => onPick(s)} className="rounded-2xl border border-[var(--line)] bg-[var(--panel)] px-4 py-3.5 text-left text-sm shadow-[var(--shadow-sm)] transition hover:-translate-y-px hover:border-[var(--line-strong)] hover:shadow-[var(--shadow)]">
              {s}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
        {earlier && (
          <div className="text-center">
            <button onClick={earlier.load} disabled={earlier.loading} className="rounded-full border border-[var(--line)] px-3 py-1 text-xs text-[var(--muted)] hover:bg-[var(--panel-2)]">
              {earlier.loading ? "Loading…" : "Load earlier messages"}
            </button>
          </div>
        )}
        {turns.map((t) =>
          t.role === "user" && KRUT_NOTE.test(t.text) ? (
            <KrutNote key={t.key} text={t.text} />
          ) : t.role === "user" ? (
            <div key={t.key} className="flex flex-col items-end gap-2">
              {!!t.context?.length && <ContextChips items={t.context} />}
              {!!t.attachments?.length && <AttachmentStrip items={t.attachments} onOpen={onOpenFile} />}
              {t.text.trim() && <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl bg-[var(--panel-2)] px-4 py-2.5 text-[0.925rem]">{t.text}</div>}
            </div>
          ) : (
            <div key={t.key} className="flex gap-3">
              <div className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--chrome)]">
                <img src="/favicon.png" alt="" className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                {!!t.tools?.length && <ToolTrail tools={t.tools} />}
                {t.text ? (
                  <div className="prose-krut">
                    <Markdown
                      remarkPlugins={[remarkGfm]}
                      urlTransform={mediaUrlTransform}
                      components={{
                        code: ({ children, className }) => <CodeOrFile className={className} onOpen={onOpenFile}>{children}</CodeOrFile>,
                        // A file the assistant made (from MEDIA:) downloads; it wears a paperclip.
                        a: ({ href, children }) =>
                          href?.startsWith("/api/me/assistant/files/raw") ? (
                            <a href={href} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1"><Icon name="paperclip" size={14} className="shrink-0" />{children}</a>
                          ) : (
                            <a href={href} target="_blank" rel="noreferrer noopener">{children}</a>
                          ),
                        img: ({ src, alt }) => <img src={typeof src === "string" ? src : undefined} alt={alt ?? ""} className="my-2 max-h-96 rounded-lg border border-[var(--line)]" />,
                      }}
                    >
                      {withMedia(t.text)}
                    </Markdown>
                  </div>
                ) : t.streaming && !t.tools?.some((x) => x.status === "running") ? (
                  <div className="flex gap-1 py-2">
                    {[0, 1, 2].map((i) => <span key={i} className="h-2 w-2 animate-bounce rounded-full bg-[var(--muted)]" style={{ animationDelay: `${i * 120}ms` }} />)}
                  </div>
                ) : null}
                {t.streaming && t.text && <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-[var(--muted)] align-middle" />}
                {t.error && <p className="mt-2 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-700">{t.error}</p>}
                {!t.streaming &&
                  t.tasks?.map((id) => {
                    const task = recorded.data?.tasks.find((x) => x.id === id);
                    return task ? <TaskCheck key={id} task={task} /> : null;
                  })}
              </div>
            </div>
          ),
        )}
        {waiting.map((a) => (
          <div key={a.id} className="ml-10">
            <ApprovalCard a={a} compact onDecided={onDecided} />
          </div>
        ))}
        <div ref={end} />
      </div>
    </div>
  );
}

/** Messages Krut itself puts into a chat (colleague requests and answers, approval decisions). */
const KRUT_NOTE = /^\[(Colleague request #|Answer to your request #|Request #\d+ to |Approval #)/;

function KrutNote({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const [head, ...rest] = text.split("\n");
  return (
    <div className="mx-auto w-full max-w-[85%] rounded-xl border border-dashed border-[var(--line)] px-4 py-2 text-xs text-[var(--muted)]">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 text-left">
        <Icon name="people" size={14} className="shrink-0" />
        <span className="min-w-0 flex-1 truncate font-medium">{head?.replace(/^\[|\]$/g, "")}</span>
        {rest.join("").trim() && <Icon name="chevron" size={14} className={cx("shrink-0 transition-transform", open ? "-rotate-90" : "rotate-90")} />}
      </button>
      {open && <div className="mt-2 whitespace-pre-wrap text-[var(--text)]">{rest.join("\n").trim()}</div>}
    </div>
  );
}

/** Inline code that is a workspace path becomes a clickable file; everything else stays code. */
function CodeOrFile({ children, className, onOpen }: { children: ReactNode; className?: string; onOpen: (p: string) => void }) {
  const text = String(children ?? "");
  const rel = !className && !text.includes("\n") ? workspaceRel(text) : null;
  if (rel)
    return (
      <button onClick={() => onOpen(rel)} className="rounded-md border border-[var(--line)] bg-[var(--panel)] px-1.5 py-0.5 font-mono text-[0.8em] text-[var(--color-accent)] hover:bg-[var(--panel-2)]">
        <Icon name="file" size={13} className="mr-1 -mt-0.5 inline" />
        {rel.split("/").pop()}
      </button>
    );
  return <code className={className}>{children}</code>;
}

/** Re-renders every second while `on`. */
function useTick(on: boolean) {
  const [, set] = useState(0);
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => set((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [on]);
}

const clock = (ms: number) => `${Math.floor(ms / 60_000)}:${String(Math.floor((ms % 60_000) / 1000)).padStart(2, "0")}`;

function ToolTrail({ tools }: { tools: Tool[] }) {
  const [open, setOpen] = useState(false);
  const running = tools.findLast((t) => t.status === "running");
  useTick(!!running?.startedAt);
  // Long steps (image/video generation, big jobs) look frozen without a clock.
  const elapsed = running?.startedAt ? Date.now() - running.startedAt : 0;
  return (
    <div className="mb-2 text-xs">
      <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] bg-[var(--panel)] px-2.5 py-1 text-[var(--muted)] hover:text-[var(--text)]">
        {running ? <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--color-brand)]" /> : <Icon name="check" size={13} className="text-emerald-600" />}
        {running ? `Using ${prettyTool(running.name)}…` : `Used ${tools.length} tool${tools.length > 1 ? "s" : ""}`}
        {running && elapsed >= 15_000 && <span className="tabular-nums">{clock(elapsed)}</span>}
        <Icon name="chevron" size={12} className={cx("transition-transform", open ? "-rotate-90" : "rotate-90")} />
      </button>
      {running && elapsed >= 60_000 && (
        <p className="mt-1.5 text-[var(--muted)]">Still working — some steps (like generating images or video) take a few minutes.</p>
      )}
      {open && (
        <ul className="mt-2 space-y-1 border-l-2 border-[var(--line)] pl-3">
          {tools.map((t, i) => (
            <li key={i} className="text-[var(--muted)]">
              <span className={cx("inline-flex items-center gap-1.5", t.status === "failed" && "text-red-700")}>
                {t.status === "running" ? <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--color-brand)]" /> : <Icon name={t.status === "failed" ? "x" : "check"} size={12} className={t.status === "failed" ? "" : "text-emerald-600"} />}
                {prettyTool(t.name)}
              </span>
              {t.preview && <span className="ml-2 font-mono text-[11px] opacity-80">{t.preview.slice(0, 140)}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type Pending = Attachment & { id?: string; uploading?: boolean };

function AttachmentStrip({ items, onOpen, onRemove }: { items: Pending[]; onOpen: (p: string) => void; onRemove?: (id: string) => void }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      {items.map((a) => (
        <div key={a.id ?? a.path} className="group relative">
          <button disabled={a.uploading} onClick={() => a.path && onOpen(a.path)} className="flex items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-1.5 pr-3 text-left text-xs hover:bg-[var(--panel-2)]">
            {a.uploading ? (
              <div className="grid h-10 w-10 place-items-center"><Spinner /></div>
            ) : isImage(a.path) ? (
              <img src={fileUrl(a.path)} alt="" className="h-10 w-10 rounded-lg object-cover" />
            ) : (
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-[var(--panel-2)] text-[10px] font-semibold uppercase">{a.name.split(".").pop()?.slice(0, 4)}</div>
            )}
            <span className="max-w-40 truncate">{a.name}</span>
          </button>
          {onRemove && !a.uploading && (
            <button onClick={() => onRemove(a.id ?? a.path)} className="absolute -top-1.5 -right-1.5 hidden h-5 w-5 place-items-center rounded-full bg-[var(--chrome)] text-white shadow group-hover:grid" title="Remove" aria-label="Remove"><Icon name="x" size={11} /></button>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Composer ──────────────────────────────────────────────────────────── */

function ContextChips({ items, onRemove }: { items: Picked[]; onRemove?: (path: string) => void }) {
  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {items.map((c) => (
        <span key={c.path} className="flex items-center gap-1 rounded-full border border-[var(--line)] bg-[var(--panel)] px-2.5 py-0.5 text-xs" title={`Context: ${c.path}`}>
          <Icon name={c.kind === "dir" ? "files" : "file"} size={13} className="shrink-0 text-[var(--muted)]" /> <span className="max-w-48 truncate">{c.path}</span>
          {onRemove && <button onClick={() => onRemove(c.path)} className="ml-0.5 text-[var(--muted)] hover:text-red-700" title="Remove" aria-label="Remove"><Icon name="x" size={12} /></button>}
        </span>
      ))}
    </div>
  );
}

function Composer({ busy, canUpload, onSend, onStop, onOpenFile }: { busy: boolean; canUpload: boolean; onSend: (t: string, a: Attachment[], c: Picked[]) => void; onStop: () => void; onOpenFile: (p: string) => void }) {
  const [text, setText] = useState("");
  const [context, setContext] = useState<Picked[]>([]);
  const [picking, setPicking] = useState(false);
  const [files, setFiles] = useState<Pending[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const area = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }, [text]);

  const add = async (list: FileList | File[]) => {
    if (!canUpload) return setError("File uploads aren't enabled for your group.");
    setError(null);
    await Promise.all(
      Array.from(list)
        .slice(0, 10)
        .map(async (f) => {
          const id = crypto.randomUUID();
          setFiles((fs) => [...fs, { id, path: "", name: f.name, uploading: true }]);
          try {
            const up = await uploadFile(f);
            setFiles((fs) => fs.map((x) => (x.id === id ? { id, ...up } : x)));
          } catch (e) {
            setFiles((fs) => fs.filter((x) => x.id !== id));
            setError(`${f.name}: ${(e as Error).message}`);
          }
        }),
    );
  };
  const ready = files.filter((f) => !f.uploading && f.path);
  const canSend = !busy && (text.trim() || ready.length) && !files.some((f) => f.uploading);
  const submit = () => {
    if (!canSend) return;
    onSend(text.trim(), ready.map(({ path, name }) => ({ path, name })), context);
    setText("");
    setFiles([]);
    setContext([]);
  };

  return (
    <div
      className="relative shrink-0 px-4 pb-4"
      onDragOver={(e) => {
        if (!canUpload) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        if (e.dataTransfer.files.length) void add(e.dataTransfer.files);
      }}
    >
      <div className="mx-auto max-w-3xl">
        {error && <p className="mb-2 rounded-lg bg-red-500/10 px-3 py-1.5 text-xs text-red-700">{error}</p>}
        <div className={cx("rounded-2xl border bg-[var(--panel)] shadow-[var(--shadow)] transition focus-within:border-[var(--line-strong)] focus-within:ring-[3px] focus-within:ring-[var(--color-brand)]/15", dragging ? "border-[var(--color-accent)] ring-2 ring-[var(--color-accent)]/20" : "border-[var(--line)]")}>
          {!!context.length && (
            <div className="px-3 pt-3 [&>div]:justify-start">
              <ContextChips items={context} onRemove={(p) => setContext((c) => c.filter((x) => x.path !== p))} />
            </div>
          )}
          {!!files.length && (
            <div className="px-3 pt-3">
              <AttachmentStrip items={files} onOpen={onOpenFile} onRemove={(id) => setFiles((fs) => fs.filter((f) => (f.id ?? f.path) !== id))} />
            </div>
          )}
          <textarea
            ref={area}
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit();
              }
            }}
            onPaste={(e) => {
              const imgs = Array.from(e.clipboardData.files);
              if (imgs.length) {
                e.preventDefault();
                void add(imgs);
              }
            }}
            placeholder={dragging ? "Drop files to attach" : "Message your assistant"}
            className="block w-full resize-none bg-transparent px-4 pt-3.5 pb-2 text-[0.95rem] outline-none placeholder:text-[var(--muted)]"
          />
          <div className="flex items-center justify-between px-2.5 pb-2.5">
            <div className="flex items-center">
              <button onClick={() => setPicking(true)} className="flex h-9 items-center gap-1.5 rounded-lg px-2 text-[var(--muted)] hover:bg-[var(--panel-2)] hover:text-[var(--text)]" title="Point your assistant at folders or files in your workspace">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>
                <span className="hidden text-xs sm:inline">Context</span>
              </button>
              {canUpload && (
                <>
                  <button onClick={() => input.current?.click()} className="grid h-9 w-9 place-items-center rounded-lg text-[var(--muted)] hover:bg-[var(--panel-2)] hover:text-[var(--text)]" title="Attach files">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21.4 11.6l-9.2 9.2a6 6 0 01-8.5-8.5l9.2-9.2a4 4 0 015.7 5.7l-9.2 9.2a2 2 0 01-2.8-2.8l8.5-8.5" /></svg>
                  </button>
                  <input ref={input} type="file" multiple hidden onChange={(e) => e.target.files && void add(e.target.files).then(() => (e.target.value = ""))} />
                </>
              )}
            </div>
            {busy ? (
              <button onClick={onStop} className="grid h-9 w-9 place-items-center rounded-full bg-[var(--chrome)] text-white hover:bg-[var(--chrome-2)]" title="Stop">
                <span className="h-3 w-3 rounded-sm bg-white" />
              </button>
            ) : (
              <button onClick={submit} disabled={!canSend} className="grid h-9 w-9 place-items-center rounded-full bg-[var(--chrome)] text-white transition hover:bg-[var(--chrome-2)] disabled:bg-[var(--line)]" title="Send">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
              </button>
            )}
          </div>
        </div>
        <p className="mt-2 text-center text-[11px] text-[var(--muted)]">Your assistant can make mistakes. Check important information.</p>
        {picking && (
          <WorkspacePicker
            mode="context"
            title="Use workspace folders or files as context"
            confirmLabel="Add to message"
            onClose={() => setPicking(false)}
            onPick={(p) => {
              setContext((c) => [...c, ...p.filter((x) => !c.some((y) => y.path === x.path))].slice(0, 20));
              setPicking(false);
              area.current?.focus();
            }}
          />
        )}
      </div>
    </div>
  );
}
